/**
 * Trunnion AI - Template Gallery
 * Main gallery page for browsing, filtering, previewing, and instantiating
 * workflow templates. Includes 5 built-in seed templates as defaults.
 */

import { useState, useMemo, useCallback } from 'react';
import { LayoutTemplate } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { TemplateCard } from '@/components/templates/TemplateCard';
import { TemplateCategoryFilter } from '@/components/templates/TemplateCategoryFilter';
import { TemplatePreviewModal } from '@/components/templates/TemplatePreviewModal';
import { useTemplates, useInstantiateTemplate } from '@/hooks/queries/useTemplates';
import type {
  WorkflowTemplate,
  TemplateCategory,
  TemplateFilters,
} from '@/types/template.types';
import type { ClassificationLevel } from '@/types';

// ── Seed Templates ──────────────────────────────────────────────────────
// Hardcoded defaults shown until the API is available.

const SEED_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'seed-intelligence-analysis',
    name: 'Intelligence Analysis Pipeline',
    description:
      'Multi-step intelligence analysis workflow that ingests raw data, applies NLP extraction, cross-references against known databases, generates a structured assessment, and routes to human review before dissemination.',
    category: 'intelligence',
    classificationLevel: 'SECRET',
    tags: ['analysis', 'nlp', 'assessment', 'intel'],
    graphDefinition: {
      nodes: [
        { id: 'n1', type: 'trigger', position: { x: 0, y: 0 }, data: { nodeType: 'trigger', label: 'Data Ingest Trigger', triggerType: 'webhook', inputSchema: {} } },
        { id: 'n2', type: 'agent', position: { x: 250, y: 0 }, data: { nodeType: 'agent', label: 'NLP Extraction Agent', agentId: '', agentName: 'NLP Extractor', tier: 'brain', mode: 'reasoning', classification: 'SECRET', toolCount: 3, inputMapping: {}, outputMapping: {}, timeoutSeconds: 120, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'n3', type: 'agent', position: { x: 500, y: 0 }, data: { nodeType: 'agent', label: 'Cross-Reference Agent', agentId: '', agentName: 'DB Cross-Ref', tier: 'specialist', mode: 'reasoning', classification: 'SECRET', toolCount: 2, inputMapping: {}, outputMapping: {}, timeoutSeconds: 90, retryPolicy: { maxRetries: 1, backoffMultiplier: 1 } } },
        { id: 'n4', type: 'humanGate', position: { x: 750, y: 0 }, data: { nodeType: 'humanGate', label: 'Analyst Review', description: 'Human analyst reviews assessment', approverRoles: ['analyst', 'admin'], timeoutMinutes: 60, classification: 'SECRET', requiresJustification: true } },
        { id: 'n5', type: 'terminal', position: { x: 1000, y: 0 }, data: { nodeType: 'terminal', label: 'Disseminate', terminalType: 'success', outputMapping: {}, notifyOnComplete: true } },
      ],
      edges: [
        { id: 'e1', source: 'n1', target: 'n2', type: 'dataFlow' },
        { id: 'e2', source: 'n2', target: 'n3', type: 'dataFlow' },
        { id: 'e3', source: 'n3', target: 'n4', type: 'dataFlow' },
        { id: 'e4', source: 'n4', target: 'n5', type: 'dataFlow' },
      ],
    },
    metadata: null,
    isBuiltin: true,
    useCount: 47,
    nodeCount: 5,
    createdBy: null,
    createdAt: '2025-11-01T00:00:00Z',
    updatedAt: '2025-12-15T00:00:00Z',
  },
  {
    id: 'seed-multi-source-fusion',
    name: 'Multi-Source Fusion',
    description:
      'Parallel ingestion from multiple intelligence sources (SIGINT, HUMINT, OSINT) with fusion agent that correlates and deconflicts data into a unified intelligence picture.',
    category: 'intelligence',
    classificationLevel: 'TOP_SECRET',
    tags: ['fusion', 'multi-source', 'sigint', 'humint', 'osint'],
    graphDefinition: {
      nodes: [
        { id: 'n1', type: 'trigger', position: { x: 250, y: 0 }, data: { nodeType: 'trigger', label: 'Fusion Trigger', triggerType: 'manual', inputSchema: {} } },
        { id: 'n2', type: 'parallelFork', position: { x: 250, y: 120 }, data: { nodeType: 'parallelFork', label: 'Source Fork' } },
        { id: 'n3', type: 'agent', position: { x: 0, y: 250 }, data: { nodeType: 'agent', label: 'SIGINT Processor', agentId: '', agentName: 'SIGINT Agent', tier: 'specialist', mode: 'reasoning', classification: 'TOP_SECRET', toolCount: 4, inputMapping: {}, outputMapping: {}, timeoutSeconds: 180, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'n4', type: 'agent', position: { x: 250, y: 250 }, data: { nodeType: 'agent', label: 'HUMINT Processor', agentId: '', agentName: 'HUMINT Agent', tier: 'specialist', mode: 'reasoning', classification: 'TOP_SECRET', toolCount: 2, inputMapping: {}, outputMapping: {}, timeoutSeconds: 180, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'n5', type: 'agent', position: { x: 500, y: 250 }, data: { nodeType: 'agent', label: 'OSINT Processor', agentId: '', agentName: 'OSINT Agent', tier: 'specialist', mode: 'reasoning', classification: 'SECRET', toolCount: 5, inputMapping: {}, outputMapping: {}, timeoutSeconds: 120, retryPolicy: { maxRetries: 3, backoffMultiplier: 1.5 } } },
        { id: 'n6', type: 'parallelJoin', position: { x: 250, y: 400 }, data: { nodeType: 'parallelJoin', label: 'Fusion Join', strategy: 'all', timeoutSeconds: 600 } },
        { id: 'n7', type: 'agent', position: { x: 250, y: 520 }, data: { nodeType: 'agent', label: 'Fusion Correlator', agentId: '', agentName: 'Fusion Agent', tier: 'brain', mode: 'reasoning', classification: 'TOP_SECRET', toolCount: 3, inputMapping: {}, outputMapping: {}, timeoutSeconds: 240, retryPolicy: { maxRetries: 1, backoffMultiplier: 1 } } },
        { id: 'n8', type: 'terminal', position: { x: 250, y: 650 }, data: { nodeType: 'terminal', label: 'Unified Picture', terminalType: 'success', outputMapping: {}, notifyOnComplete: true } },
      ],
      edges: [
        { id: 'e1', source: 'n1', target: 'n2', type: 'dataFlow' },
        { id: 'e2', source: 'n2', target: 'n3', type: 'dataFlow' },
        { id: 'e3', source: 'n2', target: 'n4', type: 'dataFlow' },
        { id: 'e4', source: 'n2', target: 'n5', type: 'dataFlow' },
        { id: 'e5', source: 'n3', target: 'n6', type: 'dataFlow' },
        { id: 'e6', source: 'n4', target: 'n6', type: 'dataFlow' },
        { id: 'e7', source: 'n5', target: 'n6', type: 'dataFlow' },
        { id: 'e8', source: 'n6', target: 'n7', type: 'dataFlow' },
        { id: 'e9', source: 'n7', target: 'n8', type: 'dataFlow' },
      ],
    },
    metadata: null,
    isBuiltin: true,
    useCount: 32,
    nodeCount: 8,
    createdBy: null,
    createdAt: '2025-10-15T00:00:00Z',
    updatedAt: '2025-12-10T00:00:00Z',
  },
  {
    id: 'seed-automated-osint',
    name: 'Automated OSINT',
    description:
      'Automated open-source intelligence collection pipeline that scrapes public sources, applies entity extraction, sentiment analysis, and produces structured OSINT reports on a configurable schedule.',
    category: 'cyber',
    classificationLevel: 'CUI',
    tags: ['osint', 'scraping', 'entity-extraction', 'automation'],
    graphDefinition: {
      nodes: [
        { id: 'n1', type: 'trigger', position: { x: 0, y: 0 }, data: { nodeType: 'trigger', label: 'Scheduled Scrape', triggerType: 'scheduled', schedule: '0 */6 * * *', inputSchema: {} } },
        { id: 'n2', type: 'agent', position: { x: 250, y: 0 }, data: { nodeType: 'agent', label: 'Web Scraper Agent', agentId: '', agentName: 'OSINT Scraper', tier: 'execution_proxy', mode: 'deterministic', classification: 'CUI', toolCount: 6, inputMapping: {}, outputMapping: {}, timeoutSeconds: 300, retryPolicy: { maxRetries: 3, backoffMultiplier: 2 } } },
        { id: 'n3', type: 'agent', position: { x: 500, y: 0 }, data: { nodeType: 'agent', label: 'Entity Extractor', agentId: '', agentName: 'NER Agent', tier: 'brain', mode: 'reasoning', classification: 'CUI', toolCount: 2, inputMapping: {}, outputMapping: {}, timeoutSeconds: 120, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'n4', type: 'agent', position: { x: 750, y: 0 }, data: { nodeType: 'agent', label: 'Report Generator', agentId: '', agentName: 'Report Writer', tier: 'brain', mode: 'reasoning', classification: 'CUI', toolCount: 1, inputMapping: {}, outputMapping: {}, timeoutSeconds: 90, retryPolicy: { maxRetries: 1, backoffMultiplier: 1 } } },
        { id: 'n5', type: 'terminal', position: { x: 1000, y: 0 }, data: { nodeType: 'terminal', label: 'OSINT Report Output', terminalType: 'success', outputMapping: {}, notifyOnComplete: true } },
      ],
      edges: [
        { id: 'e1', source: 'n1', target: 'n2', type: 'dataFlow' },
        { id: 'e2', source: 'n2', target: 'n3', type: 'dataFlow' },
        { id: 'e3', source: 'n3', target: 'n4', type: 'dataFlow' },
        { id: 'e4', source: 'n4', target: 'n5', type: 'dataFlow' },
      ],
    },
    metadata: null,
    isBuiltin: true,
    useCount: 89,
    nodeCount: 5,
    createdBy: null,
    createdAt: '2025-09-20T00:00:00Z',
    updatedAt: '2025-12-20T00:00:00Z',
  },
  {
    id: 'seed-approval-chain',
    name: 'Approval Chain',
    description:
      'Multi-level approval workflow for operational actions. Routes requests through sequential human gates with configurable approval roles, automatic escalation, and audit logging at each step.',
    category: 'admin',
    classificationLevel: 'UNCLASSIFIED',
    tags: ['approval', 'governance', 'human-in-the-loop', 'audit'],
    graphDefinition: {
      nodes: [
        { id: 'n1', type: 'trigger', position: { x: 0, y: 0 }, data: { nodeType: 'trigger', label: 'Request Submitted', triggerType: 'manual', inputSchema: {} } },
        { id: 'n2', type: 'humanGate', position: { x: 250, y: 0 }, data: { nodeType: 'humanGate', label: 'L1 Review', description: 'First-level supervisor review', approverRoles: ['operator'], timeoutMinutes: 120, classification: 'UNCLASSIFIED', requiresJustification: false } },
        { id: 'n3', type: 'conditional', position: { x: 500, y: 0 }, data: { nodeType: 'conditional', label: 'Needs L2?', condition: 'riskLevel > 3', conditionType: 'expression', branches: [{ label: 'Yes', targetHandleId: 'yes', condition: 'riskLevel > 3' }, { label: 'No', targetHandleId: 'no', condition: 'riskLevel <= 3' }] } },
        { id: 'n4', type: 'humanGate', position: { x: 750, y: -80 }, data: { nodeType: 'humanGate', label: 'L2 Director Review', description: 'Director-level approval for high-risk actions', approverRoles: ['engineer', 'admin'], timeoutMinutes: 240, classification: 'UNCLASSIFIED', requiresJustification: true } },
        { id: 'n5', type: 'agent', position: { x: 750, y: 80 }, data: { nodeType: 'agent', label: 'Audit Logger', agentId: '', agentName: 'Audit Agent', tier: 'console_automation', mode: 'deterministic', classification: 'UNCLASSIFIED', toolCount: 1, inputMapping: {}, outputMapping: {}, timeoutSeconds: 30, retryPolicy: { maxRetries: 2, backoffMultiplier: 1 } } },
        { id: 'n6', type: 'terminal', position: { x: 1000, y: 0 }, data: { nodeType: 'terminal', label: 'Approved', terminalType: 'success', outputMapping: {}, notifyOnComplete: true } },
      ],
      edges: [
        { id: 'e1', source: 'n1', target: 'n2', type: 'dataFlow' },
        { id: 'e2', source: 'n2', target: 'n3', type: 'dataFlow' },
        { id: 'e3', source: 'n3', target: 'n4', type: 'conditional', label: 'Yes' },
        { id: 'e4', source: 'n3', target: 'n5', type: 'conditional', label: 'No' },
        { id: 'e5', source: 'n4', target: 'n6', type: 'dataFlow' },
        { id: 'e6', source: 'n5', target: 'n6', type: 'dataFlow' },
      ],
    },
    metadata: null,
    isBuiltin: true,
    useCount: 123,
    nodeCount: 6,
    createdBy: null,
    createdAt: '2025-08-10T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
  },
  {
    id: 'seed-cyber-threat-hunt',
    name: 'Cyber Threat Hunt',
    description:
      'Proactive cyber threat hunting workflow that ingests IOCs, runs automated scans against network telemetry, correlates findings with threat intelligence feeds, and generates prioritized threat reports for SOC analysts.',
    category: 'cyber',
    classificationLevel: 'SECRET',
    tags: ['threat-hunting', 'ioc', 'soc', 'threat-intel', 'network'],
    graphDefinition: {
      nodes: [
        { id: 'n1', type: 'trigger', position: { x: 0, y: 0 }, data: { nodeType: 'trigger', label: 'IOC Ingest', triggerType: 'webhook', inputSchema: {} } },
        { id: 'n2', type: 'agent', position: { x: 250, y: 0 }, data: { nodeType: 'agent', label: 'IOC Enrichment', agentId: '', agentName: 'IOC Enricher', tier: 'specialist', mode: 'reasoning', classification: 'SECRET', toolCount: 4, inputMapping: {}, outputMapping: {}, timeoutSeconds: 60, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'n3', type: 'agent', position: { x: 500, y: 0 }, data: { nodeType: 'agent', label: 'Telemetry Scanner', agentId: '', agentName: 'Net Scanner', tier: 'execution_proxy', mode: 'deterministic', classification: 'SECRET', toolCount: 3, inputMapping: {}, outputMapping: {}, timeoutSeconds: 300, retryPolicy: { maxRetries: 1, backoffMultiplier: 1 } } },
        { id: 'n4', type: 'agent', position: { x: 750, y: 0 }, data: { nodeType: 'agent', label: 'Threat Correlator', agentId: '', agentName: 'Threat Intel Agent', tier: 'brain', mode: 'reasoning', classification: 'SECRET', toolCount: 5, inputMapping: {}, outputMapping: {}, timeoutSeconds: 180, retryPolicy: { maxRetries: 2, backoffMultiplier: 2 } } },
        { id: 'n5', type: 'conditional', position: { x: 1000, y: 0 }, data: { nodeType: 'conditional', label: 'Threat Level?', condition: 'threatScore >= 7', conditionType: 'threshold', branches: [{ label: 'Critical', targetHandleId: 'critical', condition: 'threatScore >= 7' }, { label: 'Low', targetHandleId: 'low', condition: 'threatScore < 7' }] } },
        { id: 'n6', type: 'humanGate', position: { x: 1250, y: -80 }, data: { nodeType: 'humanGate', label: 'SOC Analyst Review', description: 'Critical threat requires immediate analyst review', approverRoles: ['analyst', 'admin'], timeoutMinutes: 30, classification: 'SECRET', requiresJustification: true } },
        { id: 'n7', type: 'terminal', position: { x: 1250, y: 80 }, data: { nodeType: 'terminal', label: 'Log & Monitor', terminalType: 'success', outputMapping: {}, notifyOnComplete: false } },
        { id: 'n8', type: 'terminal', position: { x: 1500, y: -80 }, data: { nodeType: 'terminal', label: 'Incident Response', terminalType: 'success', outputMapping: {}, notifyOnComplete: true } },
      ],
      edges: [
        { id: 'e1', source: 'n1', target: 'n2', type: 'dataFlow' },
        { id: 'e2', source: 'n2', target: 'n3', type: 'dataFlow' },
        { id: 'e3', source: 'n3', target: 'n4', type: 'dataFlow' },
        { id: 'e4', source: 'n4', target: 'n5', type: 'dataFlow' },
        { id: 'e5', source: 'n5', target: 'n6', type: 'conditional', label: 'Critical' },
        { id: 'e6', source: 'n5', target: 'n7', type: 'conditional', label: 'Low' },
        { id: 'e7', source: 'n6', target: 'n8', type: 'dataFlow' },
      ],
    },
    metadata: null,
    isBuiltin: true,
    useCount: 56,
    nodeCount: 8,
    createdBy: null,
    createdAt: '2025-10-01T00:00:00Z',
    updatedAt: '2025-12-18T00:00:00Z',
  },
];

// ── Classification filter options ───────────────────────────────────────

const CLASSIFICATION_OPTIONS = [
  { value: '', label: 'All Classifications' },
  { value: 'UNCLASSIFIED', label: 'Unclassified' },
  { value: 'CUI', label: 'CUI' },
  { value: 'SECRET', label: 'Secret' },
  { value: 'TOP_SECRET', label: 'Top Secret' },
  { value: 'TS_SCI', label: 'TS/SCI' },
];

// ── Component ───────────────────────────────────────────────────────────

export function TemplateGallery() {
  const [selectedCategory, setSelectedCategory] = useState<
    TemplateCategory | 'all'
  >('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [previewTemplate, setPreviewTemplate] =
    useState<WorkflowTemplate | null>(null);

  // Build API filters
  const filters: TemplateFilters = useMemo(
    () => ({
      category: selectedCategory !== 'all' ? selectedCategory : undefined,
      classification: classificationFilter
        ? (classificationFilter as ClassificationLevel)
        : undefined,
      search: searchQuery || undefined,
    }),
    [selectedCategory, classificationFilter, searchQuery],
  );

  // Fetch templates from API
  const { data: apiData, isLoading } = useTemplates(filters);

  // Merge API results with seed defaults
  const templates = useMemo(() => {
    const apiTemplates = apiData?.items ?? [];

    // If API returned results, show those; otherwise fall back to seeds
    if (apiTemplates.length > 0) return apiTemplates;

    // Filter seed templates locally
    let filtered = [...SEED_TEMPLATES];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((t) => t.category === selectedCategory);
    }
    if (classificationFilter) {
      filtered = filtered.filter(
        (t) => t.classificationLevel === classificationFilter,
      );
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query) ||
          t.tags.some((tag) => tag.toLowerCase().includes(query)),
      );
    }
    return filtered;
  }, [apiData, selectedCategory, classificationFilter, searchQuery]);

  // Instantiation
  const instantiateMutation = useInstantiateTemplate();

  const handleUseTemplate = useCallback(
    (template: WorkflowTemplate) => {
      setPreviewTemplate(template);
    },
    [],
  );

  const handlePreview = useCallback(
    (template: WorkflowTemplate) => {
      setPreviewTemplate(template);
    },
    [],
  );

  const handleInstantiate = useCallback(
    (template: WorkflowTemplate, name: string) => {
      instantiateMutation.mutate(
        {
          templateId: template.id,
          request: {
            name,
            classificationLevel: template.classificationLevel as ClassificationLevel,
          },
        },
        {
          onSuccess: () => {
            setPreviewTemplate(null);
          },
        },
      );
    },
    [instantiateMutation],
  );

  return (
    <div className="flex h-full gap-6">
      {/* Sidebar - Category Filter */}
      <aside className="w-52 shrink-0">
        <TemplateCategoryFilter
          selected={selectedCategory}
          onChange={setSelectedCategory}
        />
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Top bar: Search + Classification filter */}
        <div className="mb-6 flex items-end gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="w-52">
            <Select
              options={CLASSIFICATION_OPTIONS}
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e.target.value)}
              placeholder="All Classifications"
            />
          </div>
        </div>

        {/* Gallery Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-52 animate-pulse rounded-xl border border-trunnion-slate-700 bg-trunnion-slate-800/30"
              />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <EmptyState
            icon={<LayoutTemplate className="h-12 w-12" />}
            message="No templates found"
            description="Try adjusting your search or filters to find workflow templates."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onUseTemplate={handleUseTemplate}
                onPreview={handlePreview}
              />
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <TemplatePreviewModal
        template={previewTemplate}
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
        onInstantiate={handleInstantiate}
        isInstantiating={instantiateMutation.isPending}
      />
    </div>
  );
}
